import { useTranslation } from 'next-i18next'
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'

import NotificationAnalytics, { NotificationEvents } from '~/analytics/notification/notificationAnalytics'
import ChecklistContainer from '~/checklist/components/ChecklistContainer'
import { isChestCompleted } from '~/checklist/helper'
import SVGIcon from '~/components/Icons/svgs/SVGIcon'
import TimesIcon from '~/components/Icons/svgs/TimesIcon'
import { useOutsideClickRef } from '~/dashboard/hooks/useOutsideClickRef'
import { selectIsNotificationListOpen, setIsNotificationListOpen } from '~/gamification/slices/gamification.slice'
import { useGetChecklist } from '~/hooks/useChecklist'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import { selectChecklistChestAnimation, setChecklistChestAnimation } from '~/redux/slices/dashboard'
import NotificationList from '~/shared/layouts/components/HeaderTop/components/NotificationList'
import { NotificationData, NotificationResponse } from '~/shared/types/notification.types'

export default function NotificationDropDown({
  notifications,
  notificationPage,
  setNotificationPage,
}: {
  notifications?: NotificationResponse
  notificationPage: number
  setNotificationPage: Dispatch<SetStateAction<number>>
}): React.ReactElement {
  const { t } = useTranslation('generics')
  const [notificationData, setNotificationData] = useState<NotificationData[]>([])
  const [windowHeight, setWindowHeight] = useState(0)
  const endOfListRef = useRef<HTMLSpanElement>(null)
  const dispatch = useAppDispatch()
  const isNotificationOpen = useAppSelector(selectIsNotificationListOpen)
  const isShowMoreBtnDisabled = notifications?.data
    ? notifications.meta.currentPage >= notifications.meta.totalPages
    : false

  const onCloseClick = (): void => {
    NotificationAnalytics.logClick(NotificationEvents.CLOSE)
    dispatch(setIsNotificationListOpen(false))
  }

  const onClickMore = (): void => {
    NotificationAnalytics.logClick(NotificationEvents.SHOW_MORE)
    if (notifications?.meta.totalPages && notificationPage < notifications.meta.totalPages) {
      setNotificationPage(notificationPage + 1)
      setTimeout(() => {
        endOfListRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
      }, 300)
    }
  }

  useEffect(() => {
    if (isNotificationOpen) {
      setWindowHeight(window.innerHeight - 100)
    }
  }, [isNotificationOpen])

  useEffect(() => {
    if (notifications?.data && JSON.stringify(notificationData) !== JSON.stringify(notifications.data)) {
      if (notifications.meta.currentPage === 1) {
        setNotificationData(notifications.data)
      } else if (
        notificationData[0]?.id > notifications.data[0]?.id &&
        !notificationData.includes(notifications.data[0])
      ) {
        setNotificationData([...notificationData, ...notifications.data])
      }
    }
  }, [notificationData, notifications])

  const ref = useOutsideClickRef(onCloseClick)
  const { data: checklist } = useGetChecklist()
  const chestAnimation = useAppSelector(selectChecklistChestAnimation)
  const checklistAvailable = !chestAnimation && checklist && !isChestCompleted(checklist)
  const handleChestClick = (): void => {
    dispatch(setChecklistChestAnimation(true))
  }

  return (
    <>
      {isNotificationOpen && (
        <div
          className={`absolute pt-4 md:pt-0  flex flex-col items-center bg-palepink w-screen h-screen sm:h-auto sm:w-85 top-0 sm:top-14 md:top-20 sm:rounded inset-x-0 ml-auto sm:mr-4 sm:shadow z-modal-top sm:z-40 ${
            isShowMoreBtnDisabled ? '' : 'pb-10 sm:pb-4'
          }`}
          ref={ref}
          style={{ maxHeight: `${windowHeight}px` }}
        >
          <span
            className="absolute flex justify-center items-center z-50 right-1 sm:right-0 sm:bg-white p-1.5 rounded-full sm:-mr-3 -mt-3 sm:-mt-6 sm:hover:scale-110 cursor-pointer duration-300 sm:border"
            onClick={onCloseClick}
          >
            <SVGIcon icon={TimesIcon} pathClassName="text-purple" size={16} />
          </span>
          {checklistAvailable && <ChecklistContainer checklist={checklist} handleChestClick={handleChestClick} />}
          <NotificationList
            checklistAvailable={checklistAvailable}
            endOfListRef={endOfListRef}
            notificationData={notificationData}
          />
          {notifications?.data && (
            <button
              className={`absolute bottom-0 sm:static bg-white text-black rounded-t px-3 py-0.5 mt-2 sm:-mb-4 origin-bottom transition drop-shadow-sharp ${
                isShowMoreBtnDisabled ? 'opacity-0 hidden' : 'hover:scale-110'
              } duration-300`}
              disabled={isShowMoreBtnDisabled}
              onClick={onClickMore}
            >
              {t('showMore')}
            </button>
          )}
        </div>
      )}
    </>
  )
}
